import React from 'react'
import { useTranslation } from 'react-i18next'
import {
    Card,
    CardBody,
} from 'reactstrap'
import { Link } from 'react-router-dom'
import Background from '../../../components/common/Background'

const corner1 = '../images/corner-1.png'
const corner2 = '../images/corner-2.png'
const corner3 = '../images/corner-3.png'

type BootstrapColor = 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'info' | 'light' | 'dark'

interface CardSummaryProps {
    /** Translation key for the card title */
    title: string
    /** Translation key for the optional link label */
    linkText?: string
    /** Link target path */
    to?: string
    /** Bootstrap colour token controlling accent classes and background image */
    color?: BootstrapColor
    children?: React.ReactNode
}

const getImage = (color: BootstrapColor): string => {
    switch (color) {
        case 'warning':
            return corner1
        case 'info':
            return corner2
        case 'success':
            return corner3
        default:
            return corner1
    }
}

const getContentClassNames = (color: BootstrapColor): string => {
    const contentClassNames = 'display-4 fs-4 mb-2 font-weight-normal text-sans-serif'
    return `${contentClassNames} text-${color}`
}

/**
 * Summary card used on the devices list dashboard.
 *
 * Renders a Falcon-styled card with a background image, a translated
 * title, a numeric/content slot, and an optional navigation link.
 */
const CardSummary = ({
    title,
    linkText,
    to = '#!',
    color = 'primary',
    children,
}: CardSummaryProps) => {
    const { t } = useTranslation()
    return (
        <Card className='mb-3 overflow-hidden' style={{ minWidth: '12rem' }}>
            <Background image={getImage(color)} className='bg-card' />
            <CardBody className='position-relative'>
                <h6>
                    {t(title)}
                </h6>
                <div className={getContentClassNames(color)}>{children}</div>
                {linkText && (
                    <Link
                        className="font-weight-semi-bold fs--1 text-nowrap"
                        to={to}
                    >
                        {t(linkText)} &#62;
                    </Link>
                )}
            </CardBody>
        </Card>
    )
}

export default CardSummary
